import { Link, mui, React, ui } from '_core';

import { FONT_SIZE } from '@local/finsera-core/src/theme/layout-size';
import { useMediaQuery } from '@mui/material';

type PrimaryNavLinkProps = {
  id: string;
  tooltip: string;
  to: string;
  onClick: () => void;
  icon: React.ReactElement;
  selected?: boolean;
  disabled?: boolean;
  disabledTooltip?: React.ReactElement;
  dark?: boolean;
};

const PrimaryNavLink: React.FC<PrimaryNavLinkProps> = ({
  id,
  tooltip,
  disabledTooltip = <></>,
  to,
  onClick,
  selected = false,
  disabled = false,
  icon,
  dark,
}) => {
  const theme = mui.styles.useTheme() as mui.core.Theme;
  const showTooltip = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);

  function handleClick(event: { currentTarget: any }) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleClose() {
    setAnchorEl(null);
  }

  const handleLinkClick = (e: React.MouseEvent) => {
    if (selected) {
      e.preventDefault();
      return;
    }
    onClick();
  };

  const classes = {
    navLink: {
      color: dark ? 'rgba(0,0,0,.87)' : 'rgba(255,255,255,.5)',
      fontWeight: '500',
      textDecoration: 'none',
      fontSize: FONT_SIZE.sm,
      padding: '6px 12px',
      borderRadius: '100px',
      display: 'flex',
      alignItems: 'center',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap' as const,
    },
    navLinkActive: {
      background: 'rgba(255,255,255,.1)',
      color: '#fff',
      fontWeight: '600',
    },
    navLinkDisabled: {
      opacity: 0.5,
    },
  };

  const linkStyle = React.useMemo(() => {
    if (disabled) return { ...classes.navLink, ...classes.navLinkDisabled };
    if (selected) return { ...classes.navLink, ...classes.navLinkActive };
    return classes.navLink;
  }, [selected, disabled]);

  if (disabled)
    return (
      <>
        <Link
          id={id}
          to="#"
          style={linkStyle}
          aria-owns={anchorEl ? 'simple-menu' : undefined}
          aria-haspopup="true"
          onClick={handleClick}
          onMouseOver={handleClick}
        >
          {tooltip}
        </Link>
        <mui.core.Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ onMouseLeave: handleClose }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
            },
          }}
        >
          <mui.core.Box sx={{ maxWidth: '300px', padding: '1.5rem 1rem 1rem' }}>
            <mui.core.IconButton
              onClick={handleClose}
              size="small"
              sx={{
                opacity: 0.9,
                position: 'absolute',
                right: '0.5rem',
                top: '0.66rem',
              }}
            >
              <mui.icons.Close />
            </mui.core.IconButton>
            {disabledTooltip}
          </mui.core.Box>
        </mui.core.Menu>
      </>
    );

  return (
    <Link id={id} to={to} onClick={handleLinkClick} style={linkStyle}>
      <ui.ConditionalTooltip title={tooltip} condition={showTooltip}>
        <mui.core.Box
          sx={{
            display: { md: 'none' },
            ...(dark && {
              color: 'rgba(0, 0, 0, 0.54)',
              textAlign: 'center',
              padding: '8px 4px',
              mr: '8px',
              width: '30px',
              borderRadius: '8px',
            }),
          }}
        >
          {icon}
        </mui.core.Box>
      </ui.ConditionalTooltip>
      <mui.core.Box sx={{ display: { sm: 'none', md: 'block' } }}>{tooltip}</mui.core.Box>
    </Link>
  );
};

export default PrimaryNavLink;
